import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LoadingSpinner, Page, PageInner } from "@app/shared";
import { QESButton, WizardNavigationBar, WizardSteps } from "@app/shared/qes-standards";
import { CreateExceptionsWizardContextSteps } from './contexts/interfaces';
import { CreateExceptionsRequestModel, CreateExceptionsTargetModel } from "./interfaces";
import { CreateExceptionsContext, CreateExceptionsProvider } from "./contexts/CreateExceptionsContext";
import ExceptionsFiltersContextProvider from "./contexts/ExceptionsFiltersContext";
import { DragAndDropMultiFileUploaderContextProvider } from "@app/shared/DragAndDropMultiFileUploaderContext"
import { FeatureFlagContext } from "@app/feature-flags";
import Features from "@app/feature-flags/features";
import ExceptionsService from "./exceptionsService";
import { focusFirstInteractiveElement } from "@app/shared/utility";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CreateExceptionsServiceArea from "./steps/CreateExceptionsServiceArea";
import CreateExceptionsSpecialties from "./steps/CreateExceptionsSpecialties";
import CreateExceptionsProjects from "./steps/CreateExceptionsProjects";
import CreateExceptionsDetails from "./steps/CreateExceptionsDetails"
import CreateExceptionsReview from "./steps/CreateExceptionsReview";
import CreateExceptionsAttachment from "./steps/CreateExceptionsAttachment";

const CreateExceptionsWizard: FC = () => {
  return (
    <ExceptionsFiltersContextProvider>
      <CreateExceptionsProvider>
        <DragAndDropMultiFileUploaderContextProvider>
          <CreateExceptionsWizardContent />
        </DragAndDropMultiFileUploaderContextProvider>
      </CreateExceptionsProvider>
    </ExceptionsFiltersContextProvider>
  );
}

const CreateExceptionsWizardContent = (): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const exceptionsService = useRef(new ExceptionsService()).current;

  const [pageIsValid, setPageIsValid] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const humanReadablePageNames = {
    [CreateExceptionsWizardContextSteps.ServiceArea]: "Service Area",
    [CreateExceptionsWizardContextSteps.Specialty]: "Specialty",
    [CreateExceptionsWizardContextSteps.Project]: "Project",
    [CreateExceptionsWizardContextSteps.Details]: "Details",
    [CreateExceptionsWizardContextSteps.Attachment]: "Attachment",
    [CreateExceptionsWizardContextSteps.Review]: "Review",
  };

  const getCurrentStep = () => {
    return wizardContext.page;
  }

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(CreateExceptionsWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as CreateExceptionsWizardContextSteps;
    wizardContext.setPage(CreateExceptionsWizardContextSteps[step] as unknown as CreateExceptionsWizardContextSteps);
  };

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case CreateExceptionsWizardContextSteps.Specialty:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.ServiceArea);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Project:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Specialty);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Details:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Project);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Attachment:
        wizardContext.setPage(CreateExceptionsWizardContextSteps.Details);
        wizardContext.setShowChangeWarning(true);
        break;
      case CreateExceptionsWizardContextSteps.Review:
        if (featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS)) {
          wizardContext.setPage(CreateExceptionsWizardContextSteps.Attachment);
        }
        else {
          wizardContext.setPage(CreateExceptionsWizardContextSteps.Details);
        }
        break;
    }
  };

  const validateAndAdvance = () => {
    let newPage: CreateExceptionsWizardContextSteps;
    switch (wizardContext.page) {
      case CreateExceptionsWizardContextSteps.ServiceArea:
        newPage = CreateExceptionsWizardContextSteps.Specialty;
        break;
      case CreateExceptionsWizardContextSteps.Specialty:
        newPage = CreateExceptionsWizardContextSteps.Project;
        break;
      case CreateExceptionsWizardContextSteps.Project:
        newPage = CreateExceptionsWizardContextSteps.Details;
        break;
      case CreateExceptionsWizardContextSteps.Details:
        if (featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS)) {
          newPage = CreateExceptionsWizardContextSteps.Attachment;
        }
        else {
          newPage = CreateExceptionsWizardContextSteps.Review;
        }
        break;
      case CreateExceptionsWizardContextSteps.Attachment:
        newPage = CreateExceptionsWizardContextSteps.Review;
        break;
    }
    if (newPage) {
      wizardContext.setPage(newPage);
      wizardContext.setShowChangeWarning(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitClicked(true);
    const regionSetId = wizardContext.selectedServiceArea.split(':')[2];
    const specialtyGroupCode = wizardContext.selectedSpecialty.split(':')[1];

    const targets: CreateExceptionsTargetModel[] = wizardContext.projects
      .filter(project => project.isChecked)
      .map(project => ({
        projectId: project.customProjectId,
        regionSetId: parseInt(regionSetId, 10),
        specialtyGroupCode,
      }));

    const model: CreateExceptionsRequestModel = {
      comment: wizardContext.comment ? wizardContext.comment : "",
      endDate: wizardContext.endDate ? wizardContext.endDate : null,
      exceptionNarrative: wizardContext.narrative ? wizardContext.narrative : "",
      previouslyApproved: wizardContext.previouslyApproved,
      specialtyGroupExceptionStatus: wizardContext.status,
      startDate: wizardContext.startDate ? wizardContext.startDate : null,
      supplementaryDocuments: wizardContext.attachments ? wizardContext.attachments : [],
      targets,
    };

    const hasAttachments = wizardContext.attachments.length > 0;

    exceptionsService.createException(model)
      .then(() => {
        toast.success("Exceptions have been successfully submitted.", {
          autoClose: 3000,
          containerId: "z-index-9999",
          onClose: () => {
            window.location.pathname = window.location.pathname.replace(/\/create$/, '/view');
          },
        });

        if (hasAttachments) {
          toast.success(`Document${wizardContext.attachments.length > 1 ? 's have' : ' has'} been successfully attached!`, {
            autoClose: 3000,
            containerId: "z-index-9999",
          });
        }
      })
      .catch(() => {
        setSubmitClicked(false);
        toast.error("There was an error processing your request. Please try again.", {
          containerId: "z-index-9999"
        });
      })
  }

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case CreateExceptionsWizardContextSteps.ServiceArea:
        return <CreateExceptionsServiceArea pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Specialty:
        return <CreateExceptionsSpecialties pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Project:
        return <CreateExceptionsProjects pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Details:
        return <CreateExceptionsDetails pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Attachment:
        return <CreateExceptionsAttachment pageIsValid={setPageIsValid} />;
      case CreateExceptionsWizardContextSteps.Review:
        return <CreateExceptionsReview pageIsValid={setPageIsValid} />;
    }
  }, [wizardContext.page]);

  useEffect(() => {
    if (!wizardContext.loading) {
      focusFirstInteractiveElement(wizardBodyRef);
    }
  }, [wizardContext.loading]);

  return (
    <Page documentTitle="New Exceptions">
      <PageInner className="create-exceptions-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">New Exceptions</h1>
        </header>

        <WizardSteps currentStep={getCurrentStep()} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />

        <div ref={wizardBodyRef}>
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>

      <WizardNavigationBar
        leftSide={(
          <Link to="/exceptions/view" className="qes-button qes-button-secondary danger" data-testid="create-exceptions-wizard__cancel">Cancel</Link>
        )}

        rightSide={(
          <div className="create-exceptions-wizard__nav-buttons">
            {wizardContext.page !== CreateExceptionsWizardContextSteps.ServiceArea &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="create-exceptions-wizard__previous">Previous</QESButton>}
            {wizardContext.page === CreateExceptionsWizardContextSteps.Review ? (
              <QESButton
                disabled={!pageIsValid || submitClicked}
                qesType="primary"
                qesTestId="create-exceptions-wizard__submit"
                className="ml-3"
                onClick={handleSubmit}>
                Submit
              </QESButton>
            ) : (
              <QESButton
                onClick={validateAndAdvance}
                disabled={!pageIsValid}
                qesTestId="create-exceptions-wizard__next"
                className="ml-3">
                Next
              </QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );

};

export default CreateExceptionsWizard;